import React from 'react';
import { FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AnnouncementBar from './AnnouncementBar';
import Header from './Header';
import Footer from './Footer';
import { serviceDetails } from '../data/services';
import { useServiceSeo } from './ServicesPage';

const ServiceDetailPage = ({ service }) => {
  const {
    eyebrow,
    name,
    subhead,
    portuguese,
    pricing,
    covers,
    note,
    faqs,
    title,
    description
  } = service;

  useServiceSeo(title, description, name);

  return (
    <PageShell>
      <section className="bg-cream-dark px-6 pb-16 pt-16 md:pb-24 md:pt-24">
        <div className="mx-auto max-w-7xl">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1 className="max-w-4xl font-fraunces text-5xl leading-tight text-navy md:text-7xl">
            {name}
          </h1>
          <p className="mt-6 max-w-4xl text-lg leading-relaxed text-navy/70 md:text-xl">
            {subhead}
          </p>
          {portuguese && (
            <p className="mt-4 font-fraunces text-2xl italic text-brass">{portuguese}</p>
          )}

          <CredentialStrip />

          {pricing && (
            <div className="mt-10 max-w-4xl rounded-2xl border border-brass/40 bg-cream p-6 text-sm leading-relaxed text-navy/80">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-brass">
                PRICING
              </span>
              {pricing}
            </div>
          )}
        </div>
      </section>

      <section className="bg-cream px-6 py-20">
        <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <Eyebrow>WHAT THIS COVERS</Eyebrow>
            <h2 className="font-fraunces text-4xl text-navy md:text-5xl">
              Documents and situations we handle.
            </h2>
          </div>

          <div>
            <ul className="space-y-4">
              {covers.map((item) => (
                <li key={item} className="flex items-start gap-3 border-b border-line pb-4 text-lg text-navy/80">
                  <SafeIcon icon={FiCheck} size={20} className="mt-1 shrink-0 text-brass" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {note && (
              <div className="mt-10 rounded-2xl border border-brass/40 bg-cream-dark p-6 text-sm leading-relaxed text-navy/80">
                {note}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-cream-dark px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="font-fraunces text-4xl text-navy md:text-5xl">
            Common questions.
          </h2>
          <div className="mt-10 space-y-4">
            {faqs.map((faq) => (
              <details key={faq.question} className="rounded-2xl border border-line bg-cream p-6">
                <summary className="cursor-pointer font-semibold text-navy">{faq.question}</summary>
                <p className="mt-4 leading-relaxed text-navy/70">
                  {faq.link ? (
                    <>
                      {faq.answer.split(' at ')[0]} at{' '}
                      <a href={faq.link} className="font-semibold text-brass underline">
                        {faq.link}
                      </a>
                      .
                    </>
                  ) : faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy px-6 py-20 text-center text-cream">
        <div className="mx-auto max-w-3xl">
          <Eyebrow>READY WHEN YOU ARE</Eyebrow>
          <h2 className="font-fraunces text-4xl md:text-5xl">Let&apos;s get it handled.</h2>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <a href="/contact" className="rounded-full bg-cream px-7 py-4 font-bold text-navy transition-colors hover:bg-brass hover:text-cream">
              Book Your Appointment
            </a>
            <a href="tel:2405713117" className="rounded-full border border-cream/50 px-7 py-4 font-bold text-cream transition-colors hover:border-brass hover:text-brass">
              Call or Text (240) 571-3117
            </a>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

const CredentialStrip = () => {
  const badges = ['MD Commissioned', 'RON Licensed', 'Bilingual EN/PT', 'E&O Insured'];

  return (
    <div className="mt-12 flex flex-wrap gap-3">
      {badges.map((badge) => (
        <span key={badge} className="inline-flex items-center gap-2 rounded-full border border-brass/40 bg-cream px-4 py-2 text-sm font-semibold text-navy">
          <SafeIcon icon={FiCheck} size={15} className="text-brass" />
          {badge}
        </span>
      ))}
    </div>
  );
};

const PageShell = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-cream selection:bg-brass selection:text-cream">
    <AnnouncementBar />
    <Header />
    <main className="flex-grow">{children}</main>
    <Footer />
  </div>
);

const Eyebrow = ({ children }) => (
  <span className="mb-5 block text-xs font-bold uppercase tracking-[0.2em] text-brass">
    {children}
  </span>
);

export default ServiceDetailPage;